/**
 * FatturatoVsBEP — grafico mensile per vedere i mesi sotto il pareggio.
 * Usa /api/analytics/bep-mensile (già esistente).
 * Barre verdi = mesi sopra BEP, rosse = sotto. Linea = BEP.
 */
import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api";
import { TrendingUp, AlertTriangle } from "lucide-react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  ReferenceLine, Legend, Cell,
} from "recharts";

const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function FatturatoVsBEP() {
  const [anno, setAnno] = useState(new Date().getFullYear());

  const { data, isLoading } = useQuery({
    queryKey: ["bep-mensile", anno],
    queryFn: () => api.get("/analytics/bep-mensile", { params: { anno } }).then((r) => r.data),
  });

  const chartData = useMemo(() => {
    if (!data?.months) return [];
    return data.months.map((m) => ({
      mese: m.label,
      fatturato: m.fatturato_effettivo || 0,
      previsto: m.fatturato_previsto || 0,
      bep: m.bep_fatturato_previsto || 0,
      distanza: m.distanza_bep_effettivo || 0,
      sotto_bep: (m.bep_fatturato_previsto || 0) > 0 && (m.fatturato_effettivo || 0) < (m.bep_fatturato_previsto || 0),
    }));
  }, [data]);

  const mesiSottoBep = chartData.filter((m) => m.sotto_bep && m.fatturato > 0).length;
  const mesiSenzaDati = chartData.filter((m) => m.fatturato === 0 && m.previsto === 0).length;

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="widget-fatturato-vs-bep">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Fatturato vs Punto di pareggio
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            Barre verdi = mesi <b>sopra</b> il BEP · barre rosse = mesi <b>sotto</b> il BEP · linea = BEP mese.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={anno} onChange={(e) => setAnno(Number(e.target.value))}
            data-testid="fvsbep-year"
            className="border border-slate-300 rounded-sm px-2 py-1 text-xs bg-white">
            {[0, -1, -2].map((d) => {
              const y = new Date().getFullYear() + d;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
          {mesiSottoBep > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-rose-100 text-rose-800 text-[10px] font-bold uppercase tracking-widest rounded-sm">
              <AlertTriangle className="w-3 h-3" /> {mesiSottoBep} mes{mesiSottoBep === 1 ? "e" : "i"} sotto pareggio
            </span>
          )}
        </div>
      </div>

      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="mese" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v, n) => [eur(v), n]} labelStyle={{ fontWeight: "bold" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <ReferenceLine y={0} stroke="#94a3b8" />
            <Bar dataKey="fatturato" name="Fatturato effettivo" radius={[3, 3, 0, 0]}>
              {chartData.map((entry, idx) => (
                <Cell key={idx} fill={entry.sotto_bep ? "#f43f5e" : entry.fatturato > 0 ? "#10b981" : "#cbd5e1"} />
              ))}
            </Bar>
            <Line type="monotone" dataKey="bep" name="BEP mensile" stroke="#1e293b" strokeWidth={2}
                  strokeDasharray="4 4" dot={{ r: 3, fill: "#1e293b" }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {mesiSenzaDati === 12 && !isLoading && (
        <div className="text-[11px] italic text-slate-400 text-center py-3">
          Nessun dato di fatturato per il {anno}. Aggiungi fatture e costi fissi per popolare il grafico.
        </div>
      )}

      {/* Sintesi anno */}
      {chartData.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 pt-3 border-t border-slate-200 text-[10px]">
          <SintesiCard label="Fatturato anno" value={eur(chartData.reduce((s, m) => s + m.fatturato, 0))} />
          <SintesiCard label="BEP totale anno" value={eur(chartData.reduce((s, m) => s + m.bep, 0))} />
          <SintesiCard label="Mesi sopra BEP" value={`${chartData.filter((m) => !m.sotto_bep && m.fatturato > 0).length} / 12`} good />
          <SintesiCard label="Mesi sotto BEP" value={`${mesiSottoBep} / 12`} bad={mesiSottoBep > 0} />
        </div>
      )}
    </div>
  );
}

const SintesiCard = ({ label, value, good = false, bad = false }) => (
  <div className={`p-2 rounded-sm ${bad ? "bg-rose-50" : good ? "bg-emerald-50" : "bg-slate-50"}`}>
    <div className={`uppercase tracking-widest font-bold ${bad ? "text-rose-700" : good ? "text-emerald-700" : "text-slate-500"}`}>{label}</div>
    <div className={`text-sm font-black tabular-nums mt-0.5 ${bad ? "text-rose-900" : good ? "text-emerald-900" : "text-slate-900"}`}>{value}</div>
  </div>
);
